import axios, { AxiosResponse, AxiosRequestConfig, AxiosInstance } from 'axios';
import * as register from './register';
import * as login from './login';
import * as forgotPassword from './forgotPassword';
import * as account from './account';
import { message } from 'antd';
import { map } from 'lodash';
import { ApiError, ERROR_TYPES } from './errorTypes';
import i18n from '../i18n';

export function request(service: AxiosInstance) {

  const baseUrl = process.env.REACT_APP_API_URL;
  console.log('BASE URL: ', baseUrl);

  return (url: string, options: AxiosRequestConfig, token?: string) => {
    return service(
      `${baseUrl}${url}`,
      {
        ...options,
        timeout: options.timeout ?? 60000,
      },
    ).catch(checkStatus);
  };

}

/**
 * Detects if an error response indicates rate limiting
 * @param response - Axios error response
 * @param statusCode - HTTP status code to check for
 * @param errorMessages - Array of error message patterns to match
 * @returns true if rate limiting detected
 */
const isRateLimitError = (response: any, statusCode: number, errorMessages: string[]): boolean => {
  // Check HTTP status code
  if (response?.status === statusCode) {
    // For status 401, we need to also check the error message to distinguish from normal auth failures
    // Extract errors from either location
    const errors = response?.data?.errors || response?.data?.payload?.errors;

    if (!errors) {
      return false;
    }

    // Convert errors to array of strings
    const errorArray = typeof errors === 'string' ? [errors] : errors;

    // Check if any error message matches rate limit patterns
    return errorArray.some((error: string) =>
      errorMessages.some(pattern =>
        error.toLowerCase().includes(pattern.toLowerCase())
      )
    );
  }

  return false;
};

const checkStatus = (result: any) => {

  const { response } = result;
  if (response?.status === 400 || response?.status === 302 || response?.status === 401 || response?.status === 403 || response?.status === 404 || response?.status === 405 || response?.status === 500) {

    console.log('RESPONSE: ', response);

    // Configuration: Rate limiting detection parameters
    const RATE_LIMIT_STATUS_CODE = 401; // Current status code for rate limiting
    const RATE_LIMIT_ERROR_PATTERNS = ['account blocked', 'blocked']; // Error message patterns to detect

    // Check for duplicate account error (suppress toast, let caller handle)
    const allErrors: string[] = (() => {
      const e = response?.data?.errors || response?.data?.payload?.errors;
      if (!e) return [];
      return typeof e === 'string' ? [e] : e;
    })();
    const isDuplicateAccount = allErrors.some((e: string) =>
      e.toLowerCase().includes('duplicate account') || e.toLowerCase().includes('duplicate')
    );
    if (isDuplicateAccount) {
      throw new ApiError(
        allErrors[0] || 'Duplicate account',
        ERROR_TYPES.DUPLICATE_ACCOUNT,
        response?.status,
        response
      );
    }

    // Check if this is a rate limiting error
    const isRateLimited = isRateLimitError(response, RATE_LIMIT_STATUS_CODE, RATE_LIMIT_ERROR_PATTERNS);

    // Handle rate limiting with special message
    if (isRateLimited) {
      // Get current language and use appropriate message
      const currentLanguage = i18n.language || 'se';
      const rateLimitMessages: Record<string, string> = {
        en: 'You have tried to log in more than 5 times in a few minutes. For safety reasons the account is not accessible for 15 minutes. Please contact us on support@farmersplan.com if you need assistance',
        se: 'Du har försökt logga in mer än 5 gånger på några minuter. Av säkerhetsskäl är kontot inte tillgängligt i 15 minuter. Kontakta oss på support@farmersplan.com om du behöver hjälp'
      };
      const rateLimitMessage = rateLimitMessages[currentLanguage] || rateLimitMessages.se;

      setTimeout(() => {
        message.error({
          content: rateLimitMessage,
          duration: 10, // Longer duration for important security message
        });
      }, 100);

      console.log('RATE LIMIT ERROR DETECTED: ', response);

      // Throw custom error with type information
      throw new ApiError(
        rateLimitMessage,
        ERROR_TYPES.RATE_LIMITED,
        response?.status,
        response
      );
    }

    const suppressErrorToast = Boolean(result?.config?.suppressErrorToast);

    // Handle other errors (existing logic)
    if (!suppressErrorToast) setTimeout(() => {
      if (response?.data?.errors) {

        const errors = response?.data?.errors;

        if (typeof errors == 'string') {

          message.error({
            content: response?.data?.errors,
            duration: 8,
          });

        }

        if (errors && errors.length && typeof errors !== 'string' && Array.isArray(errors)) {

          map(errors, (error: any) => {

            message.error({
              content: error,
              duration: 8,
            });

          });

        }

      }

      if (response?.data?.payload?.errors && response?.data?.payload?.errors.length) {

        const errors = response?.data?.payload?.errors;

        map(errors, (error: any) => {

          message.error({
            content: error,
            duration: 8,
          });

        });

      }
    }, 100);

    console.log('RESPONSE: ', response);

    // throw error to Rollbar (generic error)
    throw new ApiError(
      response?.statusText || 'API Error',
      ERROR_TYPES.GENERIC,
      response?.status,
      response
    );

  }

  return new Error(response?.statusText);
}

const instance = ({token}: {token?: string}) => {

  const options = {
    // baseURL: '/api/v1',
    timeout: 60000,
    headers: {
      'Content-type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    }
  }

  return axios.create(options);

}

export const service = async (url: string, options: AxiosRequestConfig, token?: string) => {

  const instanceOptions = token ? {token} : {};

  const requestInstance = request(instance(instanceOptions));

  const response: any = await requestInstance(url, options);

  return response.data.payload;  

  // return requestInstance(url, options).then((response: any) => response.data.payload);

}

const all = {
  register,
  login,
  forgotPassword,
  account,
};

export default all;
